import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import useLaunchpadWS from '@/hooks/useLaunchpadWS';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Rocket, Zap, Plus, ExternalLink, Timer, Flame, Trophy, TrendingUp, MessageCircle, Send, Crown } from 'lucide-react';

function ProjectCard({ p, onOpen }) {
  const statusColor = { live: '#0ECB81', approved: '#FCD535', ended: '#848E9C', pending: '#B7BDC6', rejected: '#F6465D' }[p.computed_status] || '#848E9C';
  const now = new Date();
  const startsIn = Math.max(0, Math.floor((new Date(p.start_at) - now) / 3600000));
  const endsIn = Math.max(0, Math.floor((new Date(p.end_at) - now) / 3600000));
  const isHot = (p.vol_24h_usdt || 0) > 0 && p.computed_status === 'live';
  return (
    <button onClick={onOpen}
      className="text-left border border-[#2B3139] bg-[#181A20] p-4 hover:border-[#FCD535] transition-colors flex flex-col gap-3 w-full relative"
      data-testid={`launch-project-${p.project_id}`}>
      {isHot && (
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-[#F6465D] text-white text-[9px] font-mono uppercase px-1.5 py-0.5 animate-pulse" data-testid={`hot-badge-${p.project_id}`}>
          <Flame className="w-2.5 h-2.5" /> Hot
        </div>
      )}
      <div className="flex items-center gap-3">
        {p.image ? <img src={p.image} alt="" className="w-10 h-10 rounded-full flex-shrink-0" /> : <div className="w-10 h-10 rounded-full bg-[#2B3139] grid place-items-center text-[#FCD535] font-bold flex-shrink-0">{p.symbol[0]}</div>}
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm truncate">{p.name}</div>
          <div className="text-xs uppercase font-mono text-[#B7BDC6]">{p.symbol}</div>
        </div>
        <div className="text-[10px] font-mono uppercase px-1.5 py-0.5 border" style={{ color: statusColor, borderColor: statusColor + '55', backgroundColor: statusColor + '15' }}>
          {p.computed_status}
        </div>
      </div>
      <p className="text-xs text-[#B7BDC6] line-clamp-2 min-h-[32px]">{p.description || 'No description'}</p>
      <div>
        <div className="flex items-center justify-between text-[10px] uppercase text-[#5E6673] mb-1 font-mono">
          <span>Progress</span>
          <span className="text-[#FCD535]">{p.progress_pct.toFixed(1)}%</span>
        </div>
        <div className="w-full h-1.5 bg-[#2B3139] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#FCD535] to-[#F0B90B] transition-all duration-700 ease-out"
            style={{ width: `${Math.min(100, p.progress_pct)}%` }}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div><span className="text-[#848E9C]">Hardcap:</span> <span className="font-mono text-[#FCD535]">{p.hardcap_usdt.toLocaleString()}u</span></div>
        <div><span className="text-[#848E9C]">Users:</span> <span className="font-mono text-white">{p.participant_count}</span></div>
        {isHot && (
          <div className="col-span-2 flex items-center gap-1 text-[#F6465D]">
            <TrendingUp className="w-3 h-3" /> <span className="font-mono">+{p.vol_24h_usdt.toLocaleString()}u</span> <span className="text-[#848E9C]">last 24h</span>
          </div>
        )}
        <div className="col-span-2 flex items-center gap-1 text-[#848E9C]"><Timer className="w-3 h-3" /> {p.computed_status === 'live' ? `Ends in ${endsIn}h` : p.computed_status === 'approved' ? `Starts in ${startsIn}h` : p.computed_status === 'ended' ? 'Sale complete' : 'Awaiting approval'}</div>
      </div>
    </button>
  );
}

function CreateProjectDialog({ open, onClose, onCreated, defaultMode }) {
  const [form, setForm] = useState({
    name: '', symbol: '', mode: defaultMode || 'launchpool', description: '',
    image: '', website: '', total_supply: 1000000, tokens_for_sale: 100000,
    price_usdt: 0.1, min_commit_usdt: 10, max_commit_usdt: 500, hardcap_usdt: 10000,
    start_at: new Date(Date.now() + 3600000).toISOString().slice(0, 16),
    end_at: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
  });
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (defaultMode) setForm(f => ({ ...f, mode: defaultMode })); }, [defaultMode]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post('/launchpad/projects', {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
      });
      toast.success('Project submitted — pending admin approval');
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSubmitting(false);
  };

  if (!open) return null;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-[#181A20] border border-[#2B3139] p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-launch-dialog">
        <h3 className="text-white text-xl font-semibold mb-4">Launch a New Token</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="text-xs uppercase text-[#848E9C]">Mode</label>
            <div className="mt-1 flex gap-2">
              {['launchpool', 'launchpad'].map(m => (
                <button key={m} onClick={() => set('mode', m)}
                  className={`flex-1 py-2 text-xs font-mono border rounded-sm ${form.mode === m ? 'border-[#FCD535] bg-[#FCD535] text-[#0B0E11] font-semibold' : 'border-[#2B3139] bg-[#0B0E11] text-[#B7BDC6]'}`}
                  data-testid={`create-mode-${m}`}>
                  {m === 'launchpool' ? 'Launchpool · proportional' : 'Launchpad · fixed price'}
                </button>
              ))}
            </div>
          </div>
          <div><label className="text-xs uppercase text-[#848E9C]">Name</label><Input value={form.name} onChange={e => set('name', e.target.value)} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white" data-testid="launch-name" /></div>
          <div><label className="text-xs uppercase text-[#848E9C]">Symbol</label><Input value={form.symbol} onChange={e => set('symbol', e.target.value.toUpperCase())} maxLength={10} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white font-mono uppercase" data-testid="launch-symbol" /></div>
          <div className="col-span-2"><label className="text-xs uppercase text-[#848E9C]">Description</label><Textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white" /></div>
          <div><label className="text-xs uppercase text-[#848E9C]">Logo URL</label><Input value={form.image} onChange={e => set('image', e.target.value)} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white" /></div>
          <div><label className="text-xs uppercase text-[#848E9C]">Website</label><Input value={form.website} onChange={e => set('website', e.target.value)} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white" /></div>
          <div><label className="text-xs uppercase text-[#848E9C]">Total supply</label><Input type="number" value={form.total_supply} onChange={e => set('total_supply', +e.target.value)} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white font-mono" /></div>
          <div><label className="text-xs uppercase text-[#848E9C]">Tokens for sale</label><Input type="number" value={form.tokens_for_sale} onChange={e => set('tokens_for_sale', +e.target.value)} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white font-mono" /></div>
          {form.mode === 'launchpad' && (
            <div><label className="text-xs uppercase text-[#848E9C]">Price (USDT/token)</label><Input type="number" step="0.001" value={form.price_usdt} onChange={e => set('price_usdt', +e.target.value)} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white font-mono" /></div>
          )}
          <div><label className="text-xs uppercase text-[#848E9C]">Hardcap (USDT)</label><Input type="number" value={form.hardcap_usdt} onChange={e => set('hardcap_usdt', +e.target.value)} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white font-mono" /></div>
          <div><label className="text-xs uppercase text-[#848E9C]">Min commit</label><Input type="number" value={form.min_commit_usdt} onChange={e => set('min_commit_usdt', +e.target.value)} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white font-mono" /></div>
          <div><label className="text-xs uppercase text-[#848E9C]">Max commit/user</label><Input type="number" value={form.max_commit_usdt} onChange={e => set('max_commit_usdt', +e.target.value)} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white font-mono" /></div>
          <div><label className="text-xs uppercase text-[#848E9C]">Sale start</label><Input type="datetime-local" value={form.start_at} onChange={e => set('start_at', e.target.value)} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white font-mono" /></div>
          <div><label className="text-xs uppercase text-[#848E9C]">Sale end</label><Input type="datetime-local" value={form.end_at} onChange={e => set('end_at', e.target.value)} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white font-mono" /></div>
        </div>
        <div className="flex gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="flex-1 border-[#2B3139] bg-transparent text-white hover:bg-[#2B3139]">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="flex-1 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold" data-testid="submit-launch-btn">
            {submitting ? 'Submitting…' : 'Submit for approval'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function LeaderboardPanel({ projectId, symbol }) {
  const [data, setData] = useState(null);
  const load = async () => {
    try {
      const r = await api.get(`/launchpad/projects/${projectId}/leaderboard?limit=5`);
      setData(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);  // slower polling fallback; WS drives real-time refresh
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  useLaunchpadWS(projectId, load);
  if (!data) return null;
  const leaders = data.leaders || [];
  return (
    <div className="mt-4 border border-[#2B3139] bg-[#0B0E11] p-3" data-testid="leaderboard-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs uppercase text-[#B7BDC6] font-mono">
          <Trophy className="w-3.5 h-3.5 text-[#FCD535]" /> Top Contributors
        </div>
        {data.vol_24h_usdt > 0 && (
          <div className="flex items-center gap-1 text-[10px] font-mono text-[#F6465D]">
            <Flame className="w-2.5 h-2.5" /> +{data.vol_24h_usdt.toLocaleString()}u / 24h · {data.commits_24h} commits
          </div>
        )}
      </div>
      {leaders.length === 0 ? (
        <div className="text-xs text-[#5E6673] py-2 text-center">Be the first to commit — claim rank #1.</div>
      ) : (
        <div className="flex flex-col gap-1">
          {leaders.map(l => {
            const medal = l.rank === 1 ? '🥇' : l.rank === 2 ? '🥈' : l.rank === 3 ? '🥉' : `#${l.rank}`;
            const color = l.rank === 1 ? '#FCD535' : l.rank === 2 ? '#B7BDC6' : l.rank === 3 ? '#F0B90B' : '#848E9C';
            return (
              <div key={l.user_id} className="flex items-center justify-between text-xs py-1.5 px-2 border-l-2 bg-[#181A20]" style={{ borderColor: color }} data-testid={`leader-${l.rank}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-sm w-6" style={{ color }}>{medal}</span>
                  <span className="text-white truncate">{l.name}</span>
                  <span className="text-[10px] text-[#5E6673] font-mono">{l.commits}c</span>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-[#FCD535]">{l.total_usdt.toLocaleString()}u</div>
                  <div className="text-[9px] text-[#848E9C] font-mono">~{l.tokens_estimated.toLocaleString()} {symbol}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GlobalTopRaising({ onOpen }) {
  const [items, setItems] = useState([]);
  const load = async () => {
    try {
      const r = await api.get('/launchpad/leaderboard/global?limit=3');
      setItems(r.data.projects || []);
    } catch { /* silent */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);  // slower fallback poll; WS pushes updates
    return () => clearInterval(t);
  }, []);
  useLaunchpadWS('global', load);
  if (items.length === 0) return null;
  return (
    <div className="mb-6 border border-[#FCD535]/30 bg-gradient-to-r from-[#FCD535]/8 to-transparent p-4" data-testid="global-top-raising">
      <div className="flex items-center gap-1.5 mb-3">
        <TrendingUp className="w-4 h-4 text-[#FCD535]" />
        <h3 className="text-white font-semibold text-sm uppercase tracking-wider">Top Raising Right Now</h3>
        <span className="text-[10px] font-mono text-[#5E6673]">live · updates every 20s</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {items.map((p, i) => {
          const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉';
          return (
            <button key={p.project_id} onClick={() => onOpen && onOpen(p)}
              className="text-left bg-[#181A20] border border-[#2B3139] hover:border-[#FCD535] p-3 transition-colors flex items-center gap-3"
              data-testid={`global-top-${i + 1}`}>
              <div className="text-xl">{medal}</div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold text-sm truncate">{p.name} <span className="text-[10px] text-[#B7BDC6] font-mono">{p.symbol}</span></div>
                <div className="flex items-center justify-between text-[10px] font-mono text-[#848E9C] mt-0.5">
                  <span>{p.committed_total_usdt.toLocaleString()}u raised</span>
                  {p.vol_24h_usdt > 0 && <span className="text-[#F6465D]">+{p.vol_24h_usdt.toLocaleString()}u/24h</span>}
                </div>
                <div className="mt-1.5 w-full h-1 bg-[#2B3139] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#FCD535] to-[#F0B90B] transition-all duration-700 ease-out" style={{ width: `${Math.min(100, p.progress_pct)}%` }} />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ProjectChatPanel({ projectId, isCreator, myCommittedUsdt, canPost }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/launchpad/projects/${projectId}/chat?limit=50`);
      setMessages(r.data.messages || []);
    } catch { /* silent */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [projectId]);
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/launchpad/projects/${projectId}/chat`, { text: t });
      setText('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSending(false);
  };
  return (
    <div className="mt-4 border border-[#2B3139] bg-[#0B0E11] p-3" data-testid="project-chat-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs uppercase text-[#B7BDC6] font-mono">
          <MessageCircle className="w-3.5 h-3.5 text-[#FCD535]" /> Project Chat
        </div>
        <div className="text-[9px] font-mono text-[#5E6673]">{messages.length} msgs · updates 10s</div>
      </div>
      <div className="max-h-52 overflow-y-auto flex flex-col gap-1.5 mb-2 pr-1" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="text-xs text-[#5E6673] py-4 text-center">No messages yet — start the conversation.</div>
        ) : messages.map(m => (
          <div key={m.message_id} className="bg-[#181A20] px-2.5 py-1.5 border-l-2" style={{ borderColor: m.is_creator ? '#FCD535' : m.committed_usdt > 0 ? '#0ECB81' : '#2B3139' }}>
            <div className="flex items-center gap-1.5 text-[10px] font-mono mb-0.5">
              {m.is_creator && <Crown className="w-2.5 h-2.5 text-[#FCD535]" />}
              <span className="text-white">{m.user_name}</span>
              {!m.is_creator && m.committed_usdt > 0 && (
                <span className="text-[#0ECB81] bg-[#0ECB81]/10 px-1 py-0.5 uppercase text-[9px]">{m.committed_usdt.toLocaleString()}u backer</span>
              )}
              <span className="text-[#5E6673] ml-auto">{new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <div className="text-xs text-[#EAECEF] break-words">{m.text}</div>
          </div>
        ))}
      </div>
      {canPost ? (
        <div className="flex gap-1.5">
          <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
            placeholder={isCreator ? 'Post an update as creator…' : 'Rally your backers…'}
            maxLength={500}
            className="flex-1 bg-[#181A20] border-[#2B3139] text-white text-sm h-9" data-testid="chat-input" />
          <Button onClick={send} disabled={sending || !text.trim()} className="h-9 px-3 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11]" data-testid="chat-send-btn">
            <Send className="w-3.5 h-3.5" />
          </Button>
        </div>
      ) : (
        <div className="text-[10px] text-[#848E9C] font-mono text-center py-1.5 border border-dashed border-[#2B3139]">
          Commit any USDT to unlock chat
        </div>
      )}
    </div>
  );
}

function CommitDialog({ project, onClose, onCommitted }) {
  const { user } = useAuth();
  const [usdt, setUsdt] = useState(project?.min_commit_usdt || 10);
  const [submitting, setSubmitting] = useState(false);
  const [myCommit, setMyCommit] = useState(0);
  useEffect(() => {
    if (!project?.project_id || !user) return;
    let alive = true;
    api.get(`/launchpad/projects/${project.project_id}/my_commit`).then(r => {
      if (alive) setMyCommit(r.data.total_usdt || 0);
    }).catch(() => {});
    return () => { alive = false; };
  }, [project?.project_id, user]);
  if (!project) return null;
  const isCreator = user && project.creator_id === user.user_id;
  const canPost = !!user && (isCreator || myCommit > 0);
  const tokensEst = project.mode === 'launchpad' && project.price_usdt
    ? (usdt / project.price_usdt).toFixed(4)
    : ((usdt / ((project.committed_total_usdt || 0) + usdt)) * project.tokens_for_sale).toFixed(4);
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/launchpad/projects/${project.project_id}/commit`, { usdt: Number(usdt) });
      toast.success(`Committed ${usdt} USDT · ~${tokensEst} ${project.symbol}`);
      onCommitted();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-[#181A20] border border-[#2B3139] p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="commit-dialog">
        <h3 className="text-white text-lg font-semibold mb-1">Commit to {project.name}</h3>
        <div className="text-xs text-[#B7BDC6] mb-4">{project.mode === 'launchpad' ? `Fixed price: ${project.price_usdt} USDT / ${project.symbol}` : 'Proportional pool — allocation split by USDT weight'}</div>
        <label className="text-xs uppercase text-[#848E9C]">Amount (min {project.min_commit_usdt} · max {project.max_commit_usdt} USDT)</label>
        <Input type="number" value={usdt} onChange={e => setUsdt(e.target.value)} min={project.min_commit_usdt} max={project.max_commit_usdt} className="mt-1 bg-[#0B0E11] border-[#2B3139] text-white font-mono" data-testid="commit-amount" />
        <div className="mt-3 border border-[#0ECB81]/30 bg-[#0ECB81]/8 p-2.5 text-sm">
          Estimated allocation: <span className="font-mono text-[#0ECB81]" data-testid="commit-estimate">{tokensEst} {project.symbol}</span>
        </div>
        <LeaderboardPanel projectId={project.project_id} symbol={project.symbol} />
        <ProjectChatPanel projectId={project.project_id} isCreator={isCreator} myCommittedUsdt={myCommit} canPost={canPost} />
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 border-[#2B3139] bg-transparent text-white hover:bg-[#2B3139]">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="flex-1 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold" data-testid="confirm-commit-btn">
            {submitting ? 'Committing…' : `Commit ${usdt} USDT`}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Launchpad() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('launchpool');
  const [projects, setProjects] = useState([]);
  const [creating, setCreating] = useState(false);
  const [committing, setCommitting] = useState(null);

  const load = async () => {
    const r = await api.get(`/launchpad/projects?mode=${tab}`);
    setProjects(r.data.projects || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Rocket className="w-6 h-6 text-[#FCD535]" />
            <h1 className="font-display text-white text-4xl font-bold tracking-tighter">Launchpad</h1>
          </div>
          <p className="text-[#848E9C] text-sm mt-2 max-w-2xl">
            Discover and back new tokens — commit USDT before launch and receive tokens straight into your wallet once the sale ends.
          </p>
        </div>
        {user && (
          <Button onClick={() => setCreating(true)} className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold" data-testid="launch-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Launch your token
          </Button>
        )}
      </div>

      <GlobalTopRaising onOpen={(p) => setCommitting(p)} />

      <div className="inline-flex border border-[#2B3139] p-0.5 rounded-sm bg-[#181A20] mb-6" data-testid="launch-tabs">
        {[
          { k: 'launchpool', label: 'Launchpool', icon: <Rocket className="w-3.5 h-3.5" />, desc: 'Proportional pool' },
          { k: 'launchpad', label: 'Launchpad', icon: <Zap className="w-3.5 h-3.5" />, desc: 'Fixed price' },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`launch-tab-${t.k}`}
            className={`px-4 py-2 text-sm font-semibold rounded-sm flex items-center gap-2 ${tab === t.k ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6] hover:text-white'}`}>
            {t.icon} {t.label} <span className="text-[10px] opacity-70 hidden sm:inline">· {t.desc}</span>
          </button>
        ))}
      </div>

      {projects.length === 0 ? (
        <div className="border border-[#2B3139] bg-[#181A20] p-10 text-center text-sm text-[#848E9C]">
          No {tab} projects yet. {user ? 'Be the first to launch a token.' : 'Log in and be the first to launch a token.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(p => <ProjectCard key={p.project_id} p={p} onOpen={() => setCommitting(p)} />)}
        </div>
      )}

      <CreateProjectDialog open={creating} defaultMode={tab} onClose={() => setCreating(false)} onCreated={load} />
      {committing && committing.computed_status === 'live'
        ? <CommitDialog project={committing} onClose={() => setCommitting(null)} onCommitted={load} />
        : null}
      {committing && committing.computed_status !== 'live' && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => setCommitting(null)}>
          <div className="bg-[#181A20] border border-[#2B3139] p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-white text-lg font-semibold mb-2">{committing.name}</h3>
            <div className="text-xs text-[#B7BDC6] mb-3">{committing.description || 'No description'}</div>
            <div className="text-sm text-[#EAECEF]">Status: <span className="uppercase font-mono text-[#FCD535]">{committing.computed_status}</span></div>
            {committing.website && <a href={committing.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-[#FCD535] text-sm hover:text-white"><ExternalLink className="w-3.5 h-3.5" /> Visit website</a>}
            <LeaderboardPanel projectId={committing.project_id} symbol={committing.symbol} />
            {committing.computed_status !== 'pending' && committing.computed_status !== 'rejected' && (
              <ProjectChatPanel projectId={committing.project_id}
                isCreator={user && committing.creator_id === user.user_id}
                myCommittedUsdt={0}
                canPost={!!user && (user.user_id === committing.creator_id)} />
            )}
            <Button onClick={() => setCommitting(null)} className="w-full mt-4 bg-[#2B3139] hover:bg-[#3B4149] text-white rounded-sm">Close</Button>
          </div>
        </div>
      )}
    </div>
  );
}
